import React from 'react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Plus, Trash2, Edit, Save, X, FileText, ExternalLink, Upload, Link as LinkIcon } from 'lucide-react';

const TermsItem = ({ terms, isEditing, editingTerms, setEditingTerms, onUpdate, onView, onEdit, onDelete }) => (
  <div className="flex items-center justify-between bg-black/30 p-4 rounded-lg border border-orange-500/20">
    <div className="flex items-center space-x-4 flex-1">
      <FileText className="w-5 h-5 text-orange-400" />
      {isEditing ? (
        <div className="flex-1 space-y-2">
          <Input
            value={editingTerms.title}
            onChange={(e) => setEditingTerms({ ...editingTerms, title: e.target.value })}
            placeholder="Document Title"
            className="bg-black/50 border-orange-500/30"
          />
          <Input
            value={editingTerms.url}
            onChange={(e) => setEditingTerms({ ...editingTerms, url: e.target.value })}
            placeholder="Document URL"
            className="bg-black/50 border-orange-500/30"
          />
        </div>
      ) : (
        <div className="flex-1">
          <p className="text-white font-semibold">{terms.title}</p>
          <a href={terms.url} target="_blank" rel="noopener noreferrer"
            className="text-orange-400 hover:text-orange-300 text-sm truncate block hover:underline">
            {terms.url}
          </a>
          <p className="text-xs text-gray-500 mt-1">{terms.active ? 'Active' : 'Inactive'}</p>
        </div>
      )}
    </div>

    <div className="flex gap-2">
      {isEditing ? (
        <>
          <Button onClick={() => onUpdate(terms.id, editingTerms)} size="sm" className="bg-green-500 hover:bg-green-600">
            <Save className="w-4 h-4" />
          </Button>
          <Button onClick={() => setEditingTerms(null)} size="sm" variant="outline">
            <X className="w-4 h-4" />
          </Button>
        </>
      ) : (
        <>
          <Button onClick={() => onView(terms.url)} size="sm" className="bg-blue-500 hover:bg-blue-600" title="View Document">
            <ExternalLink className="w-4 h-4" />
          </Button>
          <Button onClick={() => onEdit(terms)} size="sm" className="bg-orange-500 hover:bg-orange-600">
            <Edit className="w-4 h-4" />
          </Button>
          <Button onClick={() => onDelete(terms)} size="sm" variant="destructive">
            <Trash2 className="w-4 h-4" />
          </Button>
        </>
      )}
    </div>
  </div>
);

export const TermsSection = ({
  termsList, editingTerms, setEditingTerms,
  isAddingTerms, setIsAddingTerms,
  newTerms, setNewTerms,
  uploadingTerms, onFileUpload,
  onAdd, onUpdate, onDelete
}) => {
  return (
    <>
      <Card className="bg-black/50 border-orange-500/30 p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-orange-400">Terms &amp; Conditions Documents</h3>
          <Button onClick={() => setIsAddingTerms(true)} className="bg-orange-500 hover:bg-orange-600">
            <Plus className="w-4 h-4 mr-2" /> Add Document
          </Button>
        </div>

        <div className="space-y-3">
          {termsList.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No Terms &amp; Conditions documents added yet</p>
          ) : (
            termsList.map((terms) => (
              <TermsItem
                key={terms.id}
                terms={terms}
                isEditing={editingTerms?.id === terms.id}
                editingTerms={editingTerms}
                setEditingTerms={setEditingTerms}
                onUpdate={onUpdate}
                onView={(url) => window.open(url, '_blank')}
                onEdit={setEditingTerms}
                onDelete={onDelete}
              />
            ))
          )}
        </div>
      </Card>

      {/* Add Terms Dialog */}
      <Dialog open={isAddingTerms} onOpenChange={setIsAddingTerms}>
        <DialogContent className="bg-black border-orange-500/30 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-orange-400">Add Terms &amp; Conditions Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Document Title *</label>
              <Input
                value={newTerms.title}
                onChange={(e) => setNewTerms({ ...newTerms, title: e.target.value })}
                placeholder="e.g., Terms & Conditions"
                className="bg-black/50 border-orange-500/30"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Document</label>
              <Tabs defaultValue="url" className="w-full">
                <TabsList className="grid w-full grid-cols-2 bg-black/50">
                  <TabsTrigger value="url"><LinkIcon className="w-4 h-4 mr-2" /> URL</TabsTrigger>
                  <TabsTrigger value="upload"><Upload className="w-4 h-4 mr-2" /> Upload</TabsTrigger>
                </TabsList>
                <TabsContent value="url" className="space-y-2">
                  <Input
                    value={newTerms.url}
                    onChange={(e) => setNewTerms({ ...newTerms, url: e.target.value })}
                    placeholder="https://example.com/document.pdf"
                    className="bg-black/50 border-orange-500/30"
                  />
                  <p className="text-xs text-gray-500">Enter the URL of your document (PDF, DOC, etc.)</p>
                </TabsContent>
                <TabsContent value="upload" className="space-y-2">
                  <Input
                    type="file" accept=".pdf,.doc,.docx,.txt"
                    onChange={onFileUpload} disabled={uploadingTerms}
                    className="bg-black/50 border-orange-500/30"
                  />
                  {uploadingTerms && <p className="text-sm text-orange-400">Uploading document...</p>}
                  {newTerms.url && !uploadingTerms && (
                    <div className="bg-green-500/10 border border-green-500/30 rounded p-2">
                      <p className="text-sm text-green-400">Document uploaded successfully</p>
                      <a href={newTerms.url} target="_blank" rel="noopener noreferrer"
                        className="text-xs text-orange-400 hover:underline break-all">{newTerms.url}</a>
                    </div>
                  )}
                  <p className="text-xs text-gray-500">Upload a PDF, DOC, or TXT file</p>
                </TabsContent>
              </Tabs>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="termsActive" checked={newTerms.active}
                onChange={(e) => setNewTerms({ ...newTerms, active: e.target.checked })} className="w-4 h-4" />
              <label htmlFor="termsActive" className="text-sm text-gray-300">Active (visible on website)</label>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button onClick={() => { setIsAddingTerms(false); setNewTerms({ title: '', url: '', active: true }); }} variant="outline">
                Cancel
              </Button>
              <Button onClick={onAdd} className="bg-orange-500 hover:bg-orange-600"
                disabled={!newTerms.title || !newTerms.url || uploadingTerms}>
                Add Document
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
